import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import React, { useCallback, useEffect, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { useGlobalContext } from '../../Contexts/GlobalContext'
import { login } from '../../globalHeader/functions/ams-functions'
import { AreaType, LoginState } from '../../globalHeader/GlobalHeader'
import { analyticsManager } from '../../globalHeader/services/analytics/AnalyticsManager'
import SocialLogin from '../../SocialLogin/SocialLogin'
import BaseDialog from '../Common/BaseDialog'
import ErrorBlock from '../Common/ErrorBlock'
import HidePasswordIcon from '../Icons/hidePassword'
import ShowPasswordIcon from '../Icons/showPassword'
import UserIcon from '../Icons/user'
import ForgotPassword from './ForgotPassword'
import SignUp from './SignUp'

const SignIn: React.FC = () => {
    const { onAuthClose, onLoginSuccessful, area, onGuestRegistration, loginState } = useGlobalContext()
    const [showPassword, setShowPassword] = useState(false)
    const [userIdentifier, setUserIdentifier] = useState('')
    const [password, setPassword] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const { registerDialog, unregisterDialog } = useDialogs()

    useEffect(() => {
        analyticsManager.dispatchEvent('loginPromptShown', {})
    }, [])

    const emailValid = /^\S+@\S+\.\S+$/.test(userIdentifier)
    const usernameValid =
        userIdentifier.length >= 2 &&
        userIdentifier.length <= 15 &&
        /^[a-zA-Z0-9](?:[a-zA-Z0-9]*[-._]?[a-zA-Z0-9])+$/.test(userIdentifier)

    const onLogin = useCallback(
        async (event: { preventDefault: () => void }) => {
            event.preventDefault()
            if (!!password && (emailValid || usernameValid)) {
                const loginData = await login(userIdentifier, password)
                if (loginData) {
                    if (loginData.error) {
                        setErrorMessage(loginData.error.message)
                    } else {
                        onLoginSuccessful()
                        analyticsManager.dispatchEvent('completeLogin', {
                            authType: 'email/user_name',
                            userId: loginData.user.id,
                            userName: loginData.user.user_name,
                        })
                        unregisterDialog(Dialogs.SIGNIN)
                    }
                }
            }
        },
        [emailValid, userIdentifier, password, onLoginSuccessful],
    )

    useEffect(() => {
        if ((emailValid || usernameValid) && !!password) {
            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    onLogin(event)
                }
            }
            document.addEventListener('keydown', handleKeyDown)
            return () => {
                document.removeEventListener('keydown', handleKeyDown)
            }
        }
    }, [emailValid, usernameValid, password, onLogin])

    return (
        <BaseDialog
            name={Dialogs.SIGNIN}
            title={'Hello again'}
            onClose={() => onAuthClose('loginDismissed')}
            forceBlock
        >
            {errorMessage && <ErrorBlock error={errorMessage} />}
            <TextField
                label="Enter your email or user name:"
                margin="normal"
                type="email"
                autoComplete="email"
                value={userIdentifier}
                onChange={(e) => setUserIdentifier(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                            <IconButton edge="end" disabled>
                                <UserIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <TextField
                label="Enter your password:"
                margin="normal"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                            <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <SocialLogin title="Or Log In With:" />
            <Link
                variant="body2"
                onClick={() => {
                    unregisterDialog(Dialogs.SIGNIN)
                    registerDialog(Dialogs.FORGOT_PASSWORD, <ForgotPassword />)
                }}
            >
                I forgot my password!
            </Link>
            <Button size="large" fullWidth disabled={!(emailValid || usernameValid) || !password} onClick={onLogin}>
                Log In
            </Button>
            <Stack direction="row" gap={2} justifyContent="center" alignItems="center">
                <Typography variant="body2">I don't have an account.</Typography>
                <Link
                    variant="body2"
                    onClick={() => {
                        unregisterDialog(Dialogs.SIGNIN)
                        registerDialog(Dialogs.SIGNUP, <SignUp />)
                        analyticsManager.dispatchEvent('openSignup', {
                            origin: 'loginDialog',
                        })
                    }}
                >
                    Sign Up
                </Link>
            </Stack>
            <Typography variant="body2">Or</Typography>
            <Link
                variant="body2"
                onClick={() => {
                    if (loginState !== LoginState.LOGGED_IN && area === AreaType.WEBSITE) {
                        onGuestRegistration()
                    }
                    unregisterDialog(Dialogs.SIGNIN)
                    onAuthClose('guestContinued')
                }}
            >
                Continue As A Guest
            </Link>
        </BaseDialog>
    )
}

export default SignIn
